// React&NextJS
import React, { useState, useEffect } from 'react'
// Context&Auth
import { useAuth } from '../../../context/UserContext'
// 3rd party libraries
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
// Services&Helper functions
import firestoreWriteService from '../../../services/write'
// Types
import { CondFormData } from '../../../types/formTypes'
import { Condolence } from '../../../types/types'
// Styles
import s from './Condolences.module.scss'
import classnames from 'classnames'
// Components
import { Button } from '../../core/button/Button'
import { Text } from '../../core/text/Text'
import { TextArea } from '../../core/textArea/TextArea'
import { WhiteBox } from '../../core/whiteBox/WhiteBox'
import AuthModal from '../modals/AuthModal'
import { usePlausible } from 'next-plausible'

const c = classnames.bind(s)

type CondolencesProps = {
    condolences: Array<Condolence>
    mid: string
    addCondolence?: Function
    [key: string]: unknown
}

export default function Condolences({
    condolences,
    mid,
    addCondolence,
    ...props
}: CondolencesProps) {
    const [formState, setFormState] = useState<number>(0)
    const [modalOpen, setModalOpen] = useState<boolean>(false)
    const { user } = useAuth()
    const plausible = usePlausible()

    const {
        register,
        handleSubmit,
        reset,
        control,
        trigger,
        formState: { errors },
    } = useForm<CondFormData>({
        mode: 'onBlur',
        reValidateMode: 'onChange',
        defaultValues: {
            // title: '',
            text: '',
        },
    })

    useEffect(() => {
        if (user) {
            setModalOpen(false)
        }
    }, [user])

    useEffect(() => {
        let timer = null
        if (formState === 2) {
            try {
                const condPos =
                    document.getElementById('tabs').getBoundingClientRect()
                        .top + window.scrollY
                window.scroll({
                    top: condPos - 50,
                    behavior: 'smooth',
                })
            } catch (error) {
                console.error('Unable to scroll!')
            }
            timer = setTimeout(() => {
                setFormState(0)
                reset()
            }, 1500)
        }
        return () => {
            if (timer) clearTimeout(timer)
        }
    }, [formState])

    //Function for handling submit event
    const SubmitHandler = async (data: CondFormData) => {
        setFormState(1)

        const newCondolence: Condolence =
            await firestoreWriteService.createCondolence(data, mid, user)

        if (newCondolence) {
            plausible('memories-sendaKvedju-button')

            addCondolence(newCondolence)
            setFormState(2)
        } else {
            setFormState(-1)
        }
    }

    // const formAnim = {
    //     inactive: {
    //         opacity: formState ? 1 : 0,
    //     },
    //     active: {
    //         opacity: 1,
    //     },
    //     hybrid: {
    //         opacity: [0, 1],
    //     },
    // }

    const animation = {
        hidden: {
            opacity: 0,
        },
        show: {
            opacity: 1,
        },
    }

    if (addCondolence) {
        return (
            <WhiteBox className={c(s.condolences, props.className)}>
                <Text variant="h6" className={s.header}>
                    Senda kveðju
                </Text>

                {user ? (
                    <form
                        className={s.form}
                        onSubmit={handleSubmit(SubmitHandler)}
                        id="condForm"
                    >
                        <div className={formState === 1 ? s.disabled : ''}>
                            {/* <Input
                        {...register('title', {
                            required: {
                                value: true,
                                message: 'Titil vantar',
                            },
                        })}
                        setValue={setValue}
                        control={control}
                        trigger={trigger}
                        placeholder="Þitt nafn"
                        type="text"
                        id="title"
                        name="title"
                        errorMsg={errors?.title?.message}
                    /> */}

                            <TextArea
                                placeholder="Skrifaðu kveðju hér..."
                                id="text"
                                name="text"
                                minLength={5}
                                control={control}
                                trigger={trigger}
                                errorMsg={errors?.text?.message}
                                {...register('text', {
                                    required: {
                                        value: true,
                                        message:
                                            'Kveðja verður að vera minnst 5 stafir að lengd',
                                    },
                                })}
                            />
                            {formState === 0 && (
                                <div className={s.submitBtn}>
                                    <Button>Senda kveðju</Button>
                                </div>
                            )}
                        </div>

                        {formState === -1 && (
                            <div className={s.errorState}>
                                <Text variant="p">
                                    Ekki náðist að senda kveðju!
                                </Text>
                                <Button>Senda kveðju</Button>
                            </div>
                        )}
                        {formState === 1 && (
                            <div className={s.loadContainer}>
                                <Text variant="p">Augnablik...</Text>
                                <div className={s.loader}>
                                    <div className={s.loaderInside}></div>
                                </div>
                            </div>
                        )}
                        {formState === 2 && (
                            <div className={s.successMessage}>
                                <Button disabled>Kveðja send!</Button>
                            </div>
                        )}
                    </form>
                ) : (
                    <>
                        <Text variant="p">
                            Þú þarft að vera innskráð/ur til þess að senda
                            kveðju
                        </Text>
                        <Button
                            variant="ghost"
                            onClick={() => setModalOpen(true)}
                        >
                            Innskrá
                        </Button>

                        <AuthModal
                            isOpen={modalOpen}
                            closeModal={() => setModalOpen(false)}
                        />
                    </>
                )}

                <div className={s.condolences_map}>
                    {condolences.map((c: Condolence, i: number) => {
                        if (c.visible)
                            return (
                                <motion.div
                                    variants={animation}
                                    initial="hidden"
                                    animate="show"
                                    transition={{
                                        duration: 0.3,
                                    }}
                                    className={s.condolences_card}
                                    key={c.id}
                                    id={`cond-${i + 1}`}
                                >
                                    <div />
                                    <Text variant="h6">{c.sender.name}</Text>
                                    <Text variant="p-18">{c.text}</Text>
                                </motion.div>
                            )
                    })}
                </div>
                {/* <Text variant="h6">Væntanlegt</Text>
                <Text variant="p" className={s.condolencesTempText}>
                    Við vinnum nú hörðum höndum að áframhaldandi þróun vefsins.
                    Innan skamms mun einnig vera hægt að setja kveðjur inn á
                    minningarsíður Minningar.is
                </Text> */}
            </WhiteBox>
        )
    }
    // Used in preview page (form is disabled)
    return (
        <WhiteBox className={c(s.condolences, props.className)}>
            <div className={s.form}>
                <Text variant="h6">Senda kveðju</Text>
                {/* <Input
                    placeholder="Þitt nafn"
                    type="text"
                    id="title"
                    disabled={true}
                    aria-disabled={true}
                /> */}
                <TextArea
                    placeholder="Skrifaðu kveðju hér..."
                    id="text"
                    minLength={5}
                    disabled={true}
                    aria-disabled={true}
                />
                <Button type="button" disabled>
                    Senda kveðju
                </Button>
            </div>
            <div className={s.condolences_map}>
                {condolences.map((c: Condolence) => {
                    if (c.visible)
                        return (
                            <div className={s.condolences_card} key={c.id}>
                                <div />
                                <Text variant="h6">{c.sender}</Text>
                                <Text variant="p-18">{c.text}</Text>
                            </div>
                        )
                })}
            </div>
            {/* <Text variant="h6">Væntanlegt</Text>
            <Text variant="p" className={s.condolencesTempText}>
                Við vinnum nú hörðum höndum að áframhaldandi þróun vefsins.
                Innan skamms mun einnig vera hægt að setja kveðjur inn á
                minningarsíður Minningar.is
            </Text> */}
        </WhiteBox>
    )
}
